import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LISTING_STATES } from '../types';
import { vermieterApi } from '../api/vermieter';
import { useSessionFilter } from './useSessionFilter';
import {
  DEFAULT_FILTER,
  INSERATE_STATE_ORDER,
  ZUORDNUNG_ROWS,
  buildFilterParams,
  buildInitialFilter,
  computeBadgeCounts,
  isFilterActive,
  type InserateFilter,
  type SelectedVermieter,
  type InserateBadgeCounts,
} from '../types/inserateFilter';

// ─── Öffentlicher Chip-Typ ────────────────────────────────────────────────────

export interface ActiveChip {
  id: string;
  label: string;
  onRemove: () => void;
}

// ─── Hook-Optionen ────────────────────────────────────────────────────────────

export interface UseInserateFilterOptions {
  /** sessionStorage-Schlüssel – Standard: 'inserate_filter' */
  storageKey?: string;
  /** URL-SearchParams für den initialen ?state=... Override */
  searchParams?: URLSearchParams;
}

// ─── Hook-Rückgabe ────────────────────────────────────────────────────────────

export interface UseInserateFilterResult {
  filter: InserateFilter;
  setFilter: <K extends keyof InserateFilter>(key: K, val: InserateFilter[K]) => void;
  setFilterState: (val: InserateFilter | ((prev: InserateFilter) => InserateFilter)) => void;
  resetFilter: () => void;
  toggleVermieter: (v: SelectedVermieter) => void;
  vermieterSearch: string;
  setVermieterSearch: (s: string) => void;
  vermieterList: SelectedVermieter[];
  selectedVermieterIds: Set<number>;
  activeChips: ActiveChip[];
  badgeCounts: InserateBadgeCounts;
  filterParams: ReturnType<typeof buildFilterParams>;
  filterActive: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useInserateFilter(
  options: UseInserateFilterOptions = {},
): UseInserateFilterResult {
  const { storageKey = 'inserate_filter', searchParams } = options;

  // URL-Parameter haben Vorrang (Links vom Dashboard mit ?state=… oder ?is_online=…)
  const urlForce =
    searchParams?.get('state') || searchParams?.get('is_online')
      ? buildInitialFilter(searchParams)
      : undefined;

  const [filter, setFilterState] = useSessionFilter<InserateFilter>(
    storageKey,
    DEFAULT_FILTER,
    urlForce,
  );

  // Vermieter-Suche gehört zum Filter (nicht zur Seite)
  const [vermieterSearch, setVermieterSearch] = useState('');
  const { data: vermieterResults } = useQuery({
    queryKey: ['vermieter', 'filter-search', vermieterSearch],
    queryFn: () =>
      vermieterApi.getVermieter({
        search_text: vermieterSearch || undefined,
        per_page: 20,
      }),
  });

  // ── Abgeleitete Werte ───────────────────────────────────────────────────────

  const selectedVermieterIds = useMemo(
    () => new Set(filter.selectedVermieter.map(v => v.id)),
    [filter.selectedVermieter],
  );

  const vermieterList = useMemo<SelectedVermieter[]>(() => {
    const results = (vermieterResults?.items ?? []).map(v => ({
      id: v.id,
      name: v.name,
    }));
    const resultIds = new Set(results.map(v => v.id));
    const notInResults = filter.selectedVermieter.filter(v => !resultIds.has(v.id));
    return [...notInResults, ...results];
  }, [vermieterResults, filter.selectedVermieter]);

  const badgeCounts = useMemo(() => computeBadgeCounts(filter), [filter]);

  const filterParams = useMemo(() => buildFilterParams(filter), [filter]);

  const filterActive = useMemo(() => isFilterActive(filter), [filter]);

  const activeChips = useMemo<ActiveChip[]>(() => {
    const chips: ActiveChip[] = [];

    // Status
    if (filter.states.length > 0) {
      const prefix = filter.stateMode === 'exclude' ? 'Außer: ' : '';
      const labels = filter.states.map(s => LISTING_STATES[s]?.label ?? s);
      const display =
        labels.length > 2
          ? `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`
          : labels.join(', ');
      chips.push({
        id: 'states',
        label: `${prefix}${display}`,
        onRemove: () =>
          setFilterState(f => ({ ...f, states: [], stateMode: 'include' })),
      });
    }

    // Online-Status
    if (filter.isOnline !== 'alle') {
      chips.push({
        id: 'is-online',
        label: filter.isOnline === 'online' ? 'Nur online' : 'Nur offline',
        onRemove: () => setFilterState(f => ({ ...f, isOnline: 'alle' })),
      });
    }

    // Vermieter
    for (const v of filter.selectedVermieter) {
      chips.push({
        id: `v-${v.id}`,
        label: `Vermieter: ${v.name}`,
        onRemove: () =>
          setFilterState(f => ({
            ...f,
            selectedVermieter: f.selectedVermieter.filter(x => x.id !== v.id),
          })),
      });
    }

    // Lage
    if (filter.addressSearch)
      chips.push({
        id: 'addr',
        label: `Adresse: ${filter.addressSearch}`,
        onRemove: () => setFilterState(f => ({ ...f, addressSearch: '' })),
      });
    if (filter.zipcodeSearch)
      chips.push({
        id: 'plz',
        label: `PLZ: ${filter.zipcodeSearch}`,
        onRemove: () => setFilterState(f => ({ ...f, zipcodeSearch: '' })),
      });
    if (filter.citySearch)
      chips.push({
        id: 'city',
        label: `Stadt: ${filter.citySearch}`,
        onRemove: () => setFilterState(f => ({ ...f, citySearch: '' })),
      });

    // Plattformen + Typen
    for (const p of filter.selectedPlatforms) {
      chips.push({
        id: `plat-${p}`,
        label: `Plattform: ${p}`,
        onRemove: () =>
          setFilterState(f => ({
            ...f,
            selectedPlatforms: f.selectedPlatforms.filter(x => x !== p),
          })),
      });
    }
    for (const t of filter.selectedListingTypes) {
      chips.push({
        id: `typ-${t}`,
        label: `Typ: ${t}`,
        onRemove: () =>
          setFilterState(f => ({
            ...f,
            selectedListingTypes: f.selectedListingTypes.filter(x => x !== t),
          })),
      });
    }

    // Ranges
    const rangeEntries: [
      string,
      string,
      string,
      string,
      string | undefined,
      () => void,
    ][] = [
      [
        'Preis',
        filter.priceMin,
        filter.priceMax,
        '€',
        undefined,
        () => setFilterState(f => ({ ...f, priceMin: '', priceMax: '' })),
      ],
      [
        'Bewertung',
        filter.ratingMin,
        filter.ratingMax,
        '',
        undefined,
        () => setFilterState(f => ({ ...f, ratingMin: '', ratingMax: '' })),
      ],
      [
        'Größe',
        filter.sizeMin,
        filter.sizeMax,
        'm²',
        undefined,
        () => setFilterState(f => ({ ...f, sizeMin: '', sizeMax: '' })),
      ],
      [
        'Gäste',
        filter.guestsMin,
        filter.guestsMax,
        '',
        undefined,
        () => setFilterState(f => ({ ...f, guestsMin: '', guestsMax: '' })),
      ],
      [
        'Zimmer',
        filter.bedroomsMin,
        filter.bedroomsMax,
        '',
        undefined,
        () => setFilterState(f => ({ ...f, bedroomsMin: '', bedroomsMax: '' })),
      ],
    ];
    for (const [label, min, max, unit, , onRemove] of rangeEntries) {
      if (min || max) {
        const u = unit ? ` ${unit}` : '';
        const display =
          min && max
            ? `${min}–${max}${u}`
            : min
              ? `≥ ${min}${u}`
              : `≤ ${max}${u}`;
        chips.push({ id: `range-${label}`, label: `${label}: ${display}`, onRemove });
      }
    }

    // Buchungen
    if (
      filter.bookedFrom &&
      filter.bookedTo &&
      (filter.bookedMinNights || filter.bookedMaxNights)
    ) {
      const fmt = (d: string) =>
        new Date(d).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
        });
      const mn = filter.bookedMinNights;
      const mx = filter.bookedMaxNights;
      const nightsDisplay = mn && mx ? `${mn}–${mx}` : mn ? `≥${mn}` : `≤${mx}`;
      chips.push({
        id: 'booked',
        label: `Buchungen: ${nightsDisplay} Nächte (${fmt(filter.bookedFrom)}–${fmt(filter.bookedTo)})`,
        onRemove: () =>
          setFilterState(f => ({
            ...f,
            bookedFrom: '',
            bookedTo: '',
            bookedMinNights: '',
            bookedMaxNights: '',
          })),
      });
    }

    // Zuordnung
    for (const { key, label } of ZUORDNUNG_ROWS) {
      if (filter[key] !== 'any') {
        chips.push({
          id: `z-${key}`,
          label: `${label}: ${filter[key] === 'has' ? 'Vorhanden' : 'Fehlt'}`,
          onRemove: () => setFilterState(f => ({ ...f, [key]: 'any' })),
        });
      }
    }

    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const setFilter = useCallback(
    <K extends keyof InserateFilter>(key: K, val: InserateFilter[K]) =>
      setFilterState(f => ({ ...f, [key]: val })),
    [setFilterState],
  );

  const toggleVermieter = useCallback(
    (v: SelectedVermieter) =>
      setFilterState(f => ({
        ...f,
        selectedVermieter: selectedVermieterIds.has(v.id)
          ? f.selectedVermieter.filter(x => x.id !== v.id)
          : [...f.selectedVermieter, v],
      })),
    [selectedVermieterIds, setFilterState],
  );

  const resetFilter = useCallback(
    () => setFilterState(DEFAULT_FILTER),
    [setFilterState],
  );

  return {
    filter,
    setFilter,
    setFilterState,
    resetFilter,
    toggleVermieter,
    vermieterSearch,
    setVermieterSearch,
    vermieterList,
    selectedVermieterIds,
    activeChips,
    badgeCounts,
    filterParams,
    filterActive,
  };
}

// Re-export der Typen aus inserateFilter für Konsumenten des Hooks
export {
  DEFAULT_FILTER,
  INSERATE_STATE_ORDER,
  ZUORDNUNG_ROWS,
  type InserateFilter,
  type SelectedVermieter,
  type InserateBadgeCounts,
};
